import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactDOM from "react-dom";

const LS_KEY = "performance_cycles_v2";

// Font imports
const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&family=Roboto:wght@300;400;500&display=swap');`;
const fontStyle = { fontFamily: "Poppins, Roboto, sans-serif" };

const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 9999)}`;

// Modal wrapper
const Modal = ({ open, onClose, title, children, width = "max-w-2xl" }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 flex justify-center z-50 p-4"
        style={{ alignItems: "flex-start", paddingTop: "90px" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 25 }}
          className={`bg-white rounded-2xl shadow-xl w-full ${width} max-h-[80vh] overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className="p-4 border-b flex justify-between items-center bg-white sticky top-0 z-10">
            <h2 className="text-lg font-semibold">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
          </div>

          {/* Scrollable Content */}
          <div className="p-5 overflow-y-auto flex-1">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

// Stage row component
const StageRow = ({ stage, onChange, onDelete, onDragStart }) => {
  const update = (obj) => onChange({ ...stage, ...obj });

  return (
    <div
      className="border bg-white p-3 rounded-lg shadow-sm cursor-move hover:shadow-md transition-all"
      draggable
      onDragStart={(e) => onDragStart(e, stage)}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <input
            className="w-full border-b text-base font-medium px-1"
            value={stage.name}
            onChange={(e) => update({ name: e.target.value })}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Deadline</label>
              <input
                type="date"
                value={stage.deadline || ""}
                onChange={(e) => update({ deadline: e.target.value })}
                className="w-full px-2 py-1 border rounded"
              />
            </div>

            <div className="flex items-center gap-2 mt-5">
              <input
                type="checkbox"
                checked={stage.active}
                onChange={(e) => update({ active: e.target.checked })}
              />
              <label className="text-xs text-slate-600">Active</label>
            </div>
          </div>
        </div>

        <button
          onClick={() => onDelete(stage.id)}
          className="text-rose-600 hover:bg-rose-50 px-2 py-1 rounded ml-2 text-sm"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default function PerformanceCycleSetup() {
  // dummy data (only if localStorage empty)
  const dummy = [
    {
      id: uid("cycle"),
      name: "Annual Review 2025",
      status: "Draft",
      periodFrom: "2025-01-01",
      periodTo: "2025-12-31",
      frequency: "Yearly",
      stages: [
        { id: uid("s"), name: "Goal Setting", active: true, deadline: "2025-02-20" },
        { id: uid("s"), name: "Mid-Year Review", active: true, deadline: "2025-07-15" },
        { id: uid("s"), name: "Final Review", active: true, deadline: "2025-12-10" },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: uid("cycle"),
      name: "Q1 Review 2025",
      status: "Active",
      periodFrom: "2025-01-01",
      periodTo: "2025-03-31",
      frequency: "Quarterly",
      stages: [
        { id: uid("s"), name: "Goal Setting", active: true, deadline: "2025-01-15" },
        { id: uid("s"), name: "Review Meeting", active: true, deadline: "2025-03-20" },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  ];

  const [cycles, setCycles] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return dummy;
      const parsed = JSON.parse(raw);
      return parsed.length ? parsed : dummy;
    } catch {
      return dummy;
    }
  });

  const [selectedId, setSelectedId] = useState(cycles[0]?.id || null);
  const selectedCycle = cycles.find((c) => c.id === selectedId);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const dragStageRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(cycles));
  }, [cycles]);

  const newCycleObj = () => ({
    id: uid("cycle"),
    name: "New Performance Cycle",
    status: "Draft",
    description: "",
    frequency: "Yearly",
    periodFrom: "",
    periodTo: "",
    stages: [
      { id: uid("s"), name: "Goal Setting", active: true },
      { id: uid("s"), name: "Final Review", active: true },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const createCycle = () => {
    const c = newCycleObj();
    setCycles([c, ...cycles]);
    setSelectedId(c.id);
    setEditor(c);
    setIsEditorOpen(true);
  };

  const saveEditor = () => {
    setCycles((prev) =>
      prev.map((x) => (x.id === editor.id ? { ...editor, updatedAt: new Date().toISOString() } : x))
    );
    setIsEditorOpen(false);
  };

  const deleteCycle = (id) => {
    if (!window.confirm("Delete cycle?")) return;
    setCycles((c) => c.filter((x) => x.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const addStage = () => {
    setEditor({
      ...editor,
      stages: [...editor.stages, { id: uid("s"), name: "New Stage", active: true }],
    });
  };

  const updateStage = (s) => {
    setEditor({
      ...editor,
      stages: editor.stages.map((x) => (x.id === s.id ? s : x)),
    });
  };

  const removeStage = (id) => {
    setEditor({
      ...editor,
      stages: editor.stages.filter((x) => x.id !== id),
    });
  };

  const onDragStartStage = (e, stage) => {
    dragStageRef.current = stage;
  };

  const onDropStage = (e, index) => {
    const s = dragStageRef.current;
    if (!s) return;

    let arr = [...editor.stages];
    arr = arr.filter((x) => x.id !== s.id);
    arr.splice(index, 0, s);

    setEditor({ ...editor, stages: arr });
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50" style={fontStyle}>
      <style>{fontImport}</style>

      {/* HEADER + NEW BUTTON */}
      <div className="max-w-7xl mx-auto flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-semibold">Performance Cycle Setup</h1>
          <p className="text-sm text-gray-600">Annual / Quarterly cycles with workflows & templates</p>
        </div>

        {/* New Cycle Button (updated style) */}
        <button
          onClick={createCycle}
          className="px-4 py-1.5 bg-indigo-700 text-white rounded-md shadow hover:bg-indigo-800 text-sm"
        >
          + New Cycle
        </button>
      </div>

      {/* CONTENT GRID */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* SIDEBAR */}
        <div className="bg-white shadow rounded-2xl p-4 h-[75vh] overflow-y-auto">
          <h3 className="text-sm font-medium mb-2">Cycles</h3>

          <div className="space-y-2">
            {cycles.map((c) => (
              <div
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={`p-3 rounded border cursor-pointer transition-all ${
                  selectedId === c.id
                    ? "bg-indigo-50 border-indigo-300"
                    : "hover:bg-gray-100"
                }`}
              >
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-gray-500">{c.status}</div>

                <div className="flex gap-2 text-xs mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditor(c);
                      setIsEditorOpen(true);
                    }}
                    className="text-indigo-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteCycle(c.id);
                    }}
                    className="text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MAIN */}
        <div className="col-span-3 bg-white shadow rounded-2xl p-6 min-h-[75vh] overflow-y-auto">
          {!selectedCycle ? (
            <div className="text-center text-gray-500 py-10">Select or create a cycle.</div>
          ) : (
            <>
              <h2 className="text-xl font-semibold">{selectedCycle.name}</h2>
              <p className="text-xs text-gray-500 mt-1">
                Updated {new Date(selectedCycle.updatedAt).toLocaleString()}
              </p>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded border">
                  <div className="text-sm font-medium mb-1">Period</div>
                  <div className="text-xs text-gray-600">
                    {selectedCycle.periodFrom || "—"} → {selectedCycle.periodTo || "—"}
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded border">
                  <div className="text-sm font-medium mb-1">Stages</div>
                  <div className="text-xs text-gray-600">
                    {selectedCycle.stages.length} workflow stages
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* EDITOR MODAL */}
      <Modal
        open={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        title="Edit Performance Cycle"
      >
        {editor && (
          <div className="space-y-6">

            {/* Name */}
            <div>
              <label className="text-xs">Cycle Name</label>
              <input
                value={editor.name}
                onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="text-xs">Frequency</label>
              <select
                value={editor.frequency}
                onChange={(e) => setEditor({ ...editor, frequency: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              >
                <option>Yearly</option>
                <option>Quarterly</option>
                <option>Half-Yearly</option>
                <option>Custom</option>
              </select>
            </div>

            {/* Period */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs">From</label>
                <input
                  type="date"
                  value={editor.periodFrom}
                  onChange={(e) => setEditor({ ...editor, periodFrom: e.target.value })}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
              <div>
                <label className="text-xs">To</label>
                <input
                  type="date"
                  value={editor.periodTo}
                  onChange={(e) => setEditor({ ...editor, periodTo: e.target.value })}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
            </div>

            {/* Stages */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">Workflow Stages</h3>
                <button
                  onClick={addStage}
                  className="px-3 py-1 bg-indigo-700 text-white rounded text-sm"
                >
                  + Add Stage
                </button>
              </div>

              <div className="space-y-2">
                {editor.stages.map((s, i) => (
                  <div
                    key={s.id}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDropStage(e, i)}
                  >
                    <StageRow
                      stage={s}
                      onChange={updateStage}
                      onDelete={removeStage}
                      onDragStart={onDragStartStage}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Save */}
            <div className="flex justify-end gap-3 pt-3">
              <button onClick={() => setIsEditorOpen(false)} className="px-4 py-2 border rounded">
                Cancel
              </button>
              <button onClick={saveEditor} className="px-4 py-2 bg-indigo-700 text-white rounded">
                Save
              </button>
            </div>

          </div>
        )}
      </Modal>
    </div>
  );
}
